import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';

import routes from '../../data/routes';

// Pre-compute the animation frames from "Yonatan Bebchuk" -> "YB".
// Strategy: delete one char from the end of each word per frame,
// last word first, until we reach "Y B", then merge to "YB".
const buildFrames = () => {
  const first = 'Yonatan';
  const last = 'Bebchuk';
  const frames = [`${first} ${last}`];
  let f = first;
  let l = last;
  while (f.length > 1 || l.length > 1) {
    if (l.length > 1) l = l.slice(0, -1);
    else if (f.length > 1) f = f.slice(0, -1);
    frames.push(l.length > 0 ? `${f} ${l}` : f);
  }
  frames.push('YB');
  return frames;
};

// e.g. ["Yonatan Bebchuk", "Yonatan Bebchu", ... "Y B", "YB"]
const FRAMES = buildFrames();
const TICK_MS = 55; // ms per frame — fast enough to feel smooth

// Websites Navbar, displays routes defined in 'src/data/routes'
const Navigation = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // 0 = full name shown, FRAMES.length - 1 = "YB" shown
  const [frameIdx, setFrameIdx] = useState(0);
  const [animating, setAnimating] = useState(false);

  // Refs so interval callbacks always read the latest values without
  // needing to be listed as effect dependencies.
  const directionRef = useRef('idle');
  const intervalRef = useRef(null);
  const frameIdxRef = useRef(0);
  const setFrameIdxRef = useRef(setFrameIdx);
  const setAnimatingRef = useRef(setAnimating);

  // Keep ref in sync so interval closures always see the latest index.
  useEffect(() => {
    frameIdxRef.current = frameIdx;
  }, [frameIdx]);

  useEffect(() => {
    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setAnimatingRef.current(false);
      directionRef.current = 'idle';
    };

    const collapse = () => {
      if (directionRef.current === 'collapsing') return;
      stop();
      directionRef.current = 'collapsing';
      setAnimatingRef.current(true);
      intervalRef.current = setInterval(() => {
        const next = frameIdxRef.current + 1;
        if (next >= FRAMES.length) {
          frameIdxRef.current = FRAMES.length - 1;
          setFrameIdxRef.current(FRAMES.length - 1);
          stop();
          return;
        }
        frameIdxRef.current = next;
        setFrameIdxRef.current(next);
      }, TICK_MS);
    };

    const expand = () => {
      if (directionRef.current === 'expanding') return;
      stop();
      directionRef.current = 'expanding';
      setAnimatingRef.current(true);
      intervalRef.current = setInterval(() => {
        const next = frameIdxRef.current - 1;
        if (next <= 0) {
          frameIdxRef.current = 0;
          setFrameIdxRef.current(0);
          stop();
          return;
        }
        frameIdxRef.current = next;
        setFrameIdxRef.current(next);
      }, TICK_MS);
    };

    let collapsed = false;
    const onScroll = () => {
      const atTop = window.scrollY < 40;
      if (!atTop && !collapsed) {
        collapsed = true;
        collapse();
      } else if (atTop && collapsed) {
        collapsed = false;
        expand();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      stop();
    };
  }, []); // all helpers defined inside — no external deps needed

  const displayLabel = FRAMES[frameIdx];

  return (
    <header id="header">
      <h1 className="index-link">
        {routes.filter((l) => l.index).map((l) => (
          <Link
            key={l.label}
            to={l.path}
            className={animating ? 'title-animating' : ''}
          >
            {displayLabel}
          </Link>
        ))}
      </h1>
      <nav className="links">
        <ul>
          {routes.filter((l) => !l.index).map((l) => {
            if (l.label === 'Writings') {
              return (
                <li
                  key={l.label}
                  className="dropdown-container"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <NavLink
                    to={l.path}
                    className={({ isActive }) => (isActive ? 'active-link' : '')}
                  >
                    {l.label}{' '}
                    <span className="chevron">
                      {dropdownOpen ? '▲' : '▼'}
                    </span>
                  </NavLink>
                  {dropdownOpen && (
                    <div className="dropdown-menu">
                      <div className="dropdown-section">
                        <span className="dropdown-section-title">Writings</span>
                        <a
                          href={
                            'https://www.linkedin.com/in/'
                            + 'yonatan-bebchuk-271369274/recent-activity/all/'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                        >
                          LinkedIn <span className="arrow">↗</span>
                        </a>
                        <a
                          href="https://medium.com/@yonatanbebchuk"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                        >
                          Medium <span className="arrow">↗</span>
                        </a>
                        <a
                          href="https://substack.com/@yonatanbebchuk"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                        >
                          Substack <span className="arrow">↗</span>
                        </a>
                        <a
                          href="https://twitter.com/yonatanbebchuk"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                        >
                          X <span className="arrow">↗</span>
                        </a>
                      </div>
                    </div>
                  )}
                </li>
              );
            }
            return (
              <li key={l.label}>
                <NavLink
                  to={l.path}
                  className={({ isActive }) => (isActive ? 'active-link' : '')}
                >
                  {l.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Mobile Hamburger Toggle Button */}
      <button
        type="button"
        className="hamburger-toggle-btn"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      >
        {menuOpen ? '✕' : '☰'}
      </button>

      {/* Mobile Vertical Dropdown Menu */}
      {menuOpen && (
        <nav className="mobile-dropdown">
          <ul>
            {routes.filter((l) => !l.index).map((l) => (
              <li key={l.label}>
                <NavLink
                  to={l.path}
                  className={({ isActive }) => (isActive ? 'active-link' : '')}
                  onClick={() => setMenuOpen(false)}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Navigation;
